import { Bet } from 'types/Bet';
import { MatchOffer } from 'types/MatchOffer';
import { Odd } from 'types/Odd';
import { EventBet, EventBets, Slip } from 'types/Slip';

/**
 * A lost ticket's matches that have not started yet. They can go on a new slip
 * ("play the rest"); a match already under way is left out, its prematch
 * price no longer stands.
 */
export const remainingMatches = (
  slip: Slip,
  now: number = Date.now()
): EventBets[] =>
  (slip.event_bets ?? []).filter(
    (x) => !x.event.isLive && x.event.time > 0 && x.event.time * 1000 > now
  );

/**
 * Shows the button: any ticket with a match still to come. On a lost ticket
 * still under Open it reads "play the rest", otherwise "continue with this
 * ticket".
 */
export const canReplayRemaining = (slip: Slip, now: number = Date.now()) =>
  remainingMatches(slip, now).length > 0;

const sameId = (a: unknown, b: unknown) =>
  a != null && b != null && String(a) === String(b);

/**
 * The pick in today's offer for this match with the ticket's odd id, as the
 * offer shows it (market and price as of now), or null when the market is no
 * longer offered.
 */
export const findPickInOffer = (
  match: MatchOffer,
  pick: Pick<EventBet, 'oddCustomId'>
): { bet: Bet; odd: Odd } | null => {
  if (pick.oddCustomId == null || pick.oddCustomId === '') return null;
  const search = (bets: Bet[] | undefined): { bet: Bet; odd: Odd } | null => {
    for (const bet of bets ?? []) {
      const odd = bet.odds?.find((o) => sameId(o.custom_id, pick.oddCustomId));
      if (odd) return { bet, odd };
      const nested = search(bet.bets);
      if (nested) return nested;
    }
    return null;
  };
  return search([match.fullTimeBet, ...(match.bets ?? [])].filter(Boolean));
};
